import { formatAmount } from '@/service/recipe/scaling';
import type { DisplayUnit, Unit } from '@/service/recipe/types';

// How amounts are shown; stored recipes and groceries keep their own units
export type UnitSystem = 'original' | 'metric' | 'us';

export const unitSystems: { title: string; value: UnitSystem }[] = [
  { title: 'As written', value: 'original' },
  { title: 'Metric', value: 'metric' },
  { title: 'US', value: 'us' },
];

const GRAMS: Partial<Record<Unit, number>> = {
  Milligram: 0.001,
  Gram: 1,
  Kilogram: 1000,
};
const MILLILITRES: Partial<Record<Unit, number>> = {
  Millilitre: 1,
  Deciliter: 100,
  Liter: 1000,
};
const OUNCE = 28.349523125; // grams
const TEASPOON = 4.92892159375; // millilitres, US
const TABLESPOON = 3 * TEASPOON;
const CUP = 48 * TEASPOON;

export interface Quantity {
  amount: number;
  unit: DisplayUnit;
}

// Converts only between units of the same kind: weights to weights and
// volumes to volumes. Spoons are used in both systems and stay as they are,
// like pieces, pinches and other counted units. Amounts too small for the
// target units (under ⅛) are left in their own unit.
export function convertQuantity(
  amount: number,
  unit: Unit,
  system: UnitSystem,
): Quantity {
  const original = { amount, unit };
  if (!(amount > 0)) return original;
  if (system === 'metric' && unit === 'Cup(s)') {
    const ml = amount * CUP;
    return ml < 1000
      ? { amount: ml, unit: 'Millilitre' }
      : { amount: ml / 1000, unit: 'Liter' };
  }
  if (system !== 'us') return original;

  const grams = GRAMS[unit];
  if (grams) {
    const ounces = (amount * grams) / OUNCE;
    if (ounces < 1 / 8) return original;
    return ounces < 16
      ? { amount: ounces, unit: 'Ounce(s)' }
      : { amount: ounces / 16, unit: 'Pound(s)' };
  }
  const millilitres = MILLILITRES[unit];
  if (millilitres) {
    const ml = amount * millilitres;
    if (ml < TEASPOON / 8) return original;
    if (ml < TABLESPOON) return { amount: ml / TEASPOON, unit: 'Teaspoon(s)' };
    if (ml < CUP / 4) {
      return { amount: ml / TABLESPOON, unit: 'Tablespoon(s)' };
    }
    return { amount: ml / CUP, unit: 'Cup(s)' };
  }
  return original;
}

// "1 ½ Cup(s)", "300 Gram", or just the unit when there is no amount
export function formatQuantity(
  amount: number,
  unit: Unit,
  system: UnitSystem,
): string {
  const converted = convertQuantity(amount, unit, system);
  return [formatAmount(converted.amount, converted.unit), converted.unit]
    .filter(Boolean)
    .join(' ');
}
